import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { useSearchParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import type { BannerSwap } from "../../../utils/@types";
import { apiService } from "../../../api/api";
import { bannerDashboardApi } from "../../../api/bannerDashboardApi";
import BannerSwapCalendar from "../components/BannerSwapCalendar";
import SwapQueueDetailsDialog from "../components/SwapQueueDetailsDialog";
import {
  ALL_BANNER_SWAP_STATUSES,
  getBannerSwapStatusStyle,
} from "../bannerSwapStatusStyles";
import {
  buildMyChannelIdSet,
  swapTouchesMyChannels,
} from "../bannerChannelFilterUtils";
import { useSwapQueueCancellation } from "../useSwapQueueCancellation";
import { BannerDashboardTabLayout } from "../bannerDashboardLayout";

type CalendarViewFilter = "all" | "my_swaps" | "my_channels";

const CALENDAR_VIEW_OPTIONS: { label: string; value: CalendarViewFilter }[] = [
  { label: "All", value: "all" },
  { label: "My swaps only", value: "my_swaps" },
  { label: "My Channels Only", value: "my_channels" },
];

const CalendarTab: React.FC = () => {
  const toast = React.useRef<Toast>(null);
  const [bannerSwapQueue, setBannerSwapQueue] = useState<BannerSwap[]>([]);
  const [channelSettings, setChannelSettings] = useState<
    Array<{ ChannelId: string; ChannelManagerEmail?: string | string[] }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  // ?view=my_swaps | my_channels (e.g. from the home page)
  const [viewFilter, setViewFilter] = useState<CalendarViewFilter>(() => {
    const view = searchParams.get("view");
    return view === "my_swaps" || view === "my_channels" ? view : "all";
  });
  const [selectedRow, setSelectedRow] = useState<BannerSwap | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const user = useSelector(
    (state: { googleAuth?: { user?: { email?: string } } }) =>
      state.googleAuth?.user
  );
  const loggedInUserEmail = user?.email;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [swapData, settingsData] = await Promise.all([
        bannerDashboardApi.getBannerSwapQueue(),
        apiService.channelSettings.getAllChannelSettings(),
      ]);
      setBannerSwapQueue(swapData);
      setChannelSettings(
        settingsData.map((setting) => ({
          ChannelId: setting.ChannelId || setting.id || "",
          ChannelManagerEmail: setting.ChannelManagerEmail,
        }))
      );
    } catch (error: unknown) {
      if (error instanceof Error && "response" in error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 403) {
          setTimeout(() => void fetchData(), 500);
          return;
        }
      }
      toast.current?.show({
        severity: "error",
        summary: "Failed to load swaps",
        detail: "Could not fetch banner swap queue.",
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const refreshQueue = useCallback(async (focusSwapUID?: string) => {
    const data = await bannerDashboardApi.getBannerSwapQueue();
    setBannerSwapQueue(data);
    if (focusSwapUID) {
      const row = data.find((s) => s.swapUID === focusSwapUID);
      if (row) {
        setSelectedRow(row);
      }
    }
  }, []);

  const { cancelSwap, isSwapCancelling } = useSwapQueueCancellation({
    toast,
    setBannerSwapQueue,
    // Calendar has no table rows; removing from the queue updates the calendar.
    setFilteredRows: setBannerSwapQueue,
  });

  const handleCancelSwap = useCallback(
    async (swapUID: string) => {
      setDetailsOpen(false);
      setSelectedRow(null);
      await cancelSwap(swapUID);
    },
    [cancelSwap]
  );

  const myChannelIds = useMemo(() => {
    if (!loggedInUserEmail) {
      return new Set<string>();
    }
    return buildMyChannelIdSet(channelSettings, loggedInUserEmail);
  }, [channelSettings, loggedInUserEmail]);

  const visibleSwaps = useMemo(() => {
    if (viewFilter === "all" || !loggedInUserEmail) {
      return bannerSwapQueue;
    }
    if (viewFilter === "my_swaps") {
      return bannerSwapQueue.filter(
        (swap) =>
          swap.createdBy?.toLowerCase() === loggedInUserEmail.toLowerCase()
      );
    }
    return bannerSwapQueue.filter((swap) =>
      swapTouchesMyChannels(swap, myChannelIds)
    );
  }, [bannerSwapQueue, viewFilter, loggedInUserEmail, myChannelIds]);

  const handleEventClick = (swap: BannerSwap) => {
    setSelectedRow(swap);
    setDetailsOpen(true);
  };

  return (
    <BannerDashboardTabLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <SelectButton
          value={viewFilter}
          options={CALENDAR_VIEW_OPTIONS}
          onChange={(e) => setViewFilter(e.value ?? "all")}
          optionLabel="label"
          optionValue="value"
          allowEmpty={false}
        />
        <Button
          label="Refresh"
          icon="pi pi-refresh"
          severity="secondary"
          loading={loading}
          onClick={() => void fetchData()}
        />
      </div>

      <div className="banner-swap-calendar-legend">
        {ALL_BANNER_SWAP_STATUSES.map((status) => {
          const style = getBannerSwapStatusStyle(status);
          return (
            <span key={status} className="banner-swap-calendar-legend-item">
              <span
                className="banner-swap-calendar-legend-swatch"
                style={{
                  backgroundColor: style.backgroundColor,
                  borderColor: style.borderColor,
                }}
              />
              {style.label}
            </span>
          );
        })}
      </div>

      <BannerSwapCalendar
        swaps={visibleSwaps}
        onEventClick={handleEventClick}
      />

      {selectedRow && (
        <SwapQueueDetailsDialog
          rowData={selectedRow}
          bannerSwapQueue={bannerSwapQueue}
          visible={detailsOpen}
          onHide={() => setDetailsOpen(false)}
          onRefreshQueue={refreshQueue}
          onCancelSwap={handleCancelSwap}
          cancelInProgress={isSwapCancelling(selectedRow.swapUID)}
          toast={toast}
        />
      )}

      <Toast ref={toast} />
    </BannerDashboardTabLayout>
  );
};

export default CalendarTab;
